/**
 * SceneFlowDams — GPU D-Infinity flow particle system
 *
 * Flow field: WhiteboxTools D-Infinity, 4-band uint8 COG
 *   gs://centinela-test-data/openclaw/flow/flow_field_rgba.tif
 *   b1/R = sin(D-inf angle)  scaled 0–255  (128 = no flow)
 *   b2/G = cos(D-inf angle)  scaled 0–255  (128 = no flow)
 *   b3/B = log10(flow accumulation) scaled 0–255  (speed + spawn weight)
 *   b4/A = TWI scaled 0–255  (terrain wetness, spawn probability)
 *
 * Shader decode (per user spec):
 *   vec2  dir   = (tex.rg - 0.5) * 2.0;   // -1…+1 flow direction
 *   float speed = tex.b * MAX_SPEED;        // accumulation-weighted speed
 *   float wet   = tex.a;                    // TWI spawn probability
 *
 * WebGL2 VAO safety:
 *   Dedicated quadVAO — never touches MapLibre's internal VAO state.
 *   render() saves and restores MapLibre's bound VAO.
 */

import maplibregl from 'maplibre-gl';
import type { Scene, MapAdapter } from '../types';

const TITILER  = 'https://titiler-centinela-746476722093.us-central1.run.app';
const FLOW_COG = 'gs://centinela-test-data/openclaw/flow/flow_field_rgba.tif';

// Sapi concession bounding box — must match the COG extent
const BBOX = { west: 29.56, south: -16.07, east: 29.92, north: -15.58 };

// Physics texture: all 4 bands, no nodata param (uint8 COG — nodata=nan causes 500)
const FLOW_URL =
  `${TITILER}/cog/preview.png` +
  `?url=${encodeURIComponent(FLOW_COG)}` +
  `&bidx=1&bidx=2&bidx=3&bidx=4` +
  `&rescale=0,255&rescale=0,255&rescale=0,255&rescale=0,255` +
  `&max_size=512`;

// Visual tile overlays — uint8 COG uses nodata=0
const BASE_TILES = (extra: string) =>
  `${TITILER}/cog/tiles/WebMercatorQuad/{z}/{x}/{y}` +
  `?url=${encodeURIComponent(FLOW_COG)}&nodata=0${extra}`;

const FLOW_ACC_TILES = BASE_TILES('&bidx=3&rescale=0,255&colormap_name=blues');
const TWI_TILES      = BASE_TILES('&bidx=4&rescale=0,255&colormap_name=ylgnbu');

const PARTICLE_RES = 48;      // 48×48 = 2 304 slots
const N_PARTICLES  = 2000;
const MAX_SPEED    = 0.00045; // max advection per frame in normalised [0,1] coords
const DROP_RATE    = 0.004;
const TRAIL_FADE   = 0.91;

// ── GLSL shaders ──────────────────────────────────────────────────────────────

const QUAD_VS = /* glsl */`#version 300 es
layout(location = 0) in vec2 a_pos;
out vec2 v_uv;
void main() {
    v_uv        = a_pos * 0.5 + 0.5;
    gl_Position = vec4(a_pos, 0.0, 1.0);
}`;

const UPDATE_FS = /* glsl */`#version 300 es
precision highp float;

uniform sampler2D u_particles;
uniform sampler2D u_flow;
uniform float     u_speed;
uniform float     u_rand_seed;
uniform float     u_drop_rate;

in  vec2 v_uv;
out vec4 fragColor;

// 16-bit fixed-point helpers
vec2 decode(vec4 c) {
    vec4 r = floor(c * 255.0 + 0.5);
    return vec2(
        (r.r * 256.0 + r.g) / 65535.0,
        (r.b * 256.0 + r.a) / 65535.0
    );
}
vec4 encode(vec2 pos) {
    vec2 p = clamp(pos, 0.0, 1.0) * 65535.0;
    return vec4(floor(p.x / 256.0), mod(p.x, 256.0),
                floor(p.y / 256.0), mod(p.y, 256.0)) / 255.0;
}
float rand(vec2 seed) {
    return fract(sin(dot(seed, vec2(12.9898, 78.233))) * 43758.5453);
}

void main() {
    vec2 pos = decode(texture(u_particles, v_uv));

    // GeoTIFF row-0 = north; pos.y = 0 → south
    vec2 tuv = vec2(pos.x, 1.0 - pos.y);

    vec4  tex = texture(u_flow, tuv);

    // b1/R, b2/G → flow direction (-1…+1); b3/B → accumulation speed; b4/A → TWI
    vec2  dir   = (tex.rg - 0.5) * 2.0;
    float acc   = tex.b;                    // 0 at ridges, 1 at main channels
    float wet   = tex.a;                    // TWI: higher = wetter, prefer spawn here

    // No-flow / outside-domain: both direction channels near 128 and no accumulation
    bool nodata = acc < 0.01;

    vec2 newpos = pos + dir * acc * u_speed;

    bool oob  = any(lessThan(newpos, vec2(0.0))) || any(greaterThan(newpos, vec2(1.0)));
    bool drop = rand(v_uv + u_rand_seed) < u_drop_rate;

    if (oob || drop || nodata) {
        vec2 seed = v_uv + u_rand_seed;
        // Bias spawn toward wetter areas (TWI): accept if rand < wet, else plain random
        float rx = rand(seed + 0.1);
        float ry = rand(seed + 0.2);
        float rw = rand(seed + 0.3);
        // sample a candidate; if TWI there is high, keep it, else fall back to rx,ry
        vec2 cand = vec2(rx, ry);
        float candWet = texture(u_flow, vec2(cand.x, 1.0 - cand.y)).a;
        newpos = (rw < candWet) ? cand : vec2(rand(seed + 0.4), rand(seed + 0.5));
    }

    fragColor = encode(newpos);
}`;

const DRAW_VS = /* glsl */`#version 300 es
precision highp float;

uniform sampler2D u_particles;
uniform sampler2D u_flow;
uniform float     u_particles_res;
uniform vec4      u_bbox;       // west, south, east, north (degrees)
uniform mat4      u_matrix;

out float v_acc;

const float PI = 3.14159265358979;

vec2 decode(vec4 c) {
    vec4 r = floor(c * 255.0 + 0.5);
    return vec2(
        (r.r * 256.0 + r.g) / 65535.0,
        (r.b * 256.0 + r.a) / 65535.0
    );
}

void main() {
    float idx = float(gl_VertexID);
    float col = mod(idx, u_particles_res);
    float row = floor(idx / u_particles_res);

    vec4 state = texture(u_particles, (vec2(col, row) + 0.5) / u_particles_res);
    vec2 norm  = decode(state);

    float lon  = mix(u_bbox.x, u_bbox.z, norm.x);
    float lat  = mix(u_bbox.y, u_bbox.w, norm.y);

    float mx  = (lon + 180.0) / 360.0;
    float latR = lat * PI / 180.0;
    float my  = (1.0 - log(tan(PI / 4.0 + latR / 2.0)) / PI) / 2.0;

    gl_Position  = u_matrix * vec4(mx, my, 0.0, 1.0);
    gl_PointSize = 2.2;

    // Colour intensity driven by flow accumulation (b3)
    float acc = texture(u_flow, vec2(norm.x, 1.0 - norm.y)).b;
    v_acc = clamp(acc * 2.5, 0.0, 1.0);  // boost mid-channel contrast
}`;

const DRAW_FS = /* glsl */`#version 300 es
precision mediump float;

in  float v_acc;
out vec4  fragColor;

void main() {
    // Deep blue (low accumulation) → bright cyan (main drainage channels)
    vec3  col = mix(vec3(0.10, 0.30, 0.75), vec3(0.40, 0.90, 1.0), v_acc);
    float a   = mix(0.50, 0.90, v_acc);   // channels are more opaque
    fragColor = vec4(col * a, a);
}`;

const SCREEN_FS = /* glsl */`#version 300 es
precision mediump float;

uniform sampler2D u_tex;
uniform float     u_opacity;

in  vec2 v_uv;
out vec4 fragColor;

void main() {
    fragColor = texture(u_tex, v_uv) * u_opacity;
}`;

// ── WebGL helpers ─────────────────────────────────────────────────────────────

function compile(gl: WebGL2RenderingContext, type: number, src: string): WebGLShader {
  const s = gl.createShader(type)!;
  gl.shaderSource(s, src);
  gl.compileShader(s);
  if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
    const log = gl.getShaderInfoLog(s);
    gl.deleteShader(s);
    throw new Error(`Shader compile:\n${log}`);
  }
  return s;
}

function link(gl: WebGL2RenderingContext, vs: string, fs: string): WebGLProgram {
  const p  = gl.createProgram()!;
  const v  = compile(gl, gl.VERTEX_SHADER,   vs);
  const f  = compile(gl, gl.FRAGMENT_SHADER, fs);
  gl.attachShader(p, v); gl.attachShader(p, f);
  gl.linkProgram(p);
  gl.deleteShader(v); gl.deleteShader(f);
  if (!gl.getProgramParameter(p, gl.LINK_STATUS)) {
    const log = gl.getProgramInfoLog(p);
    gl.deleteProgram(p);
    throw new Error(`Program link: ${log}`);
  }
  return p;
}

function makeTex(
  gl: WebGL2RenderingContext,
  w: number, h: number,
  data?: Uint8Array | null,
  filter: number = -1,  // -1 → default LINEAR; pass gl.NEAREST for state textures
): WebGLTexture {
  const t = gl.createTexture()!;
  gl.bindTexture(gl.TEXTURE_2D, t);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S,     gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T,     gl.CLAMP_TO_EDGE);
  const f = filter === -1 ? gl.LINEAR : filter;
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, f);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, f);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, w, h, 0, gl.RGBA, gl.UNSIGNED_BYTE, data ?? null);
  gl.bindTexture(gl.TEXTURE_2D, null);
  return t;
}

function makeFBO(gl: WebGL2RenderingContext, tex: WebGLTexture): WebGLFramebuffer {
  const fbo = gl.createFramebuffer()!;
  gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
  gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, 0);
  const status = gl.checkFramebufferStatus(gl.FRAMEBUFFER);
  gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  if (status !== gl.FRAMEBUFFER_COMPLETE) throw new Error(`FBO incomplete: ${status}`);
  return fbo;
}

class UL {
  private cache = new Map<string, WebGLUniformLocation | null>();
  constructor(private gl: WebGL2RenderingContext, private prog: WebGLProgram) {}
  get(name: string): WebGLUniformLocation | null {
    if (!this.cache.has(name)) this.cache.set(name, this.gl.getUniformLocation(this.prog, name));
    return this.cache.get(name)!;
  }
}

// ── FlowParticleLayer ─────────────────────────────────────────────────────────

class FlowParticleLayer implements maplibregl.CustomLayerInterface {
  readonly id            = 'flow-particles';
  readonly type          = 'custom' as const;
  readonly renderingMode = '2d'     as const;

  private map!:  maplibregl.Map;
  private gl!:   WebGL2RenderingContext;
  private active = false;

  private updateProg!: WebGLProgram;  private ulUpdate!: UL;
  private drawProg!:   WebGLProgram;  private ulDraw!:   UL;
  private screenProg!: WebGLProgram;  private ulScreen!: UL;

  private stateA!: WebGLTexture;  private fboA!: WebGLFramebuffer;
  private stateB!: WebGLTexture;  private fboB!: WebGLFramebuffer;

  private screenA!: WebGLTexture;  private sfboA!: WebGLFramebuffer;
  private screenB!: WebGLTexture;  private sfboB!: WebGLFramebuffer;
  private screenW = 0;  private screenH = 0;

  private flowTex: WebGLTexture | null = null;
  private quadBuf!: WebGLBuffer;
  private quadVAO!: WebGLVertexArrayObject;
  private frame = 0;
  private onLoaded: (() => void) | null;

  constructor(onLoaded: () => void) { this.onLoaded = onLoaded; }

  onAdd(map: maplibregl.Map, gl: WebGL2RenderingContext): void {
    this.map    = map;
    this.gl     = gl;
    this.active = true;

    this.updateProg = link(gl, QUAD_VS, UPDATE_FS);
    this.drawProg   = link(gl, DRAW_VS, DRAW_FS);
    this.screenProg = link(gl, QUAD_VS, SCREEN_FS);
    this.ulUpdate   = new UL(gl, this.updateProg);
    this.ulDraw     = new UL(gl, this.drawProg);
    this.ulScreen   = new UL(gl, this.screenProg);

    const init   = this.randomState();
    // NEAREST filter: state textures encode 16-bit fixed-point positions.
    // Linear interpolation between neighbouring particles corrupts the values.
    this.stateA  = makeTex(gl, PARTICLE_RES, PARTICLE_RES, init,         gl.NEAREST);
    this.stateB  = makeTex(gl, PARTICLE_RES, PARTICLE_RES, init.slice(), gl.NEAREST);
    this.fboA    = makeFBO(gl, this.stateA);
    this.fboB    = makeFBO(gl, this.stateB);

    this.quadBuf = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, this.quadBuf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1,-1, 1,-1, -1,1, 1,1]), gl.STATIC_DRAW);

    this.quadVAO = gl.createVertexArray()!;
    gl.bindVertexArray(this.quadVAO);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
    gl.bindVertexArray(null);
    gl.bindBuffer(gl.ARRAY_BUFFER, null);

    this.loadFlowTex();
  }

  onRemove(_map: maplibregl.Map, gl: WebGL2RenderingContext): void {
    this.active   = false;
    this.onLoaded = null;
    [this.updateProg, this.drawProg, this.screenProg].forEach(p => gl.deleteProgram(p));
    [this.stateA, this.stateB].forEach(t => gl.deleteTexture(t));
    [this.fboA, this.fboB].forEach(f => gl.deleteFramebuffer(f));
    if (this.screenA) gl.deleteTexture(this.screenA);
    if (this.screenB) gl.deleteTexture(this.screenB);
    if (this.sfboA)   gl.deleteFramebuffer(this.sfboA);
    if (this.sfboB)   gl.deleteFramebuffer(this.sfboB);
    if (this.flowTex) gl.deleteTexture(this.flowTex);
    if (this.quadVAO) gl.deleteVertexArray(this.quadVAO);
    gl.deleteBuffer(this.quadBuf);
  }

  render(gl: WebGL2RenderingContext, options: Record<string, unknown>): void {
    if (!this.active || !this.flowTex) return;

    const savedVAO = gl.getParameter(gl.VERTEX_ARRAY_BINDING) as WebGLVertexArrayObject | null;
    const matrix   = options['modelViewProjectionMatrix'] as Float32Array;
    const cw = (gl.canvas as HTMLCanvasElement).width;
    const ch = (gl.canvas as HTMLCanvasElement).height;

    if (cw !== this.screenW || ch !== this.screenH) {
      if (this.sfboA)   gl.deleteFramebuffer(this.sfboA);
      if (this.sfboB)   gl.deleteFramebuffer(this.sfboB);
      if (this.screenA) gl.deleteTexture(this.screenA);
      if (this.screenB) gl.deleteTexture(this.screenB);
      this.screenA = makeTex(gl, cw, ch);
      this.screenB = makeTex(gl, cw, ch);
      this.sfboA   = makeFBO(gl, this.screenA);
      this.sfboB   = makeFBO(gl, this.screenB);
      this.screenW = cw;
      this.screenH = ch;
    }

    this.frame++;
    const seed = (this.frame * 0.00317) % 1.0;

    // Pass 1: UPDATE
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.fboB);
    gl.viewport(0, 0, PARTICLE_RES, PARTICLE_RES);
    gl.disable(gl.BLEND);
    gl.useProgram(this.updateProg);
    this.bindTex(0, this.stateA,  this.ulUpdate, 'u_particles');
    this.bindTex(1, this.flowTex, this.ulUpdate, 'u_flow');
    gl.uniform1f(this.ulUpdate.get('u_speed'),     MAX_SPEED);
    gl.uniform1f(this.ulUpdate.get('u_rand_seed'), seed);
    gl.uniform1f(this.ulUpdate.get('u_drop_rate'), DROP_RATE);
    this.quad(gl);
    [this.stateA, this.stateB] = [this.stateB, this.stateA];
    [this.fboA,   this.fboB  ] = [this.fboB,   this.fboA  ];

    // Pass 2a: TRAIL FADE
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.sfboB);
    gl.viewport(0, 0, cw, ch);
    gl.disable(gl.BLEND);
    gl.useProgram(this.screenProg);
    this.bindTex(0, this.screenA, this.ulScreen, 'u_tex');
    gl.uniform1f(this.ulScreen.get('u_opacity'), TRAIL_FADE);
    this.quad(gl);

    // Pass 2b: DRAW POINTS
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
    gl.useProgram(this.drawProg);
    this.bindTex(0, this.stateA,  this.ulDraw, 'u_particles');
    this.bindTex(1, this.flowTex, this.ulDraw, 'u_flow');
    gl.uniform1f(this.ulDraw.get('u_particles_res'), PARTICLE_RES);
    gl.uniform4f(this.ulDraw.get('u_bbox'), BBOX.west, BBOX.south, BBOX.east, BBOX.north);
    gl.uniformMatrix4fv(this.ulDraw.get('u_matrix'), false, matrix);
    gl.drawArrays(gl.POINTS, 0, N_PARTICLES);
    gl.disable(gl.BLEND);
    [this.screenA, this.screenB] = [this.screenB, this.screenA];
    [this.sfboA,   this.sfboB  ] = [this.sfboB,   this.sfboA  ];

    // Pass 3: COMPOSITE
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.viewport(0, 0, cw, ch);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
    gl.useProgram(this.screenProg);
    this.bindTex(0, this.screenA, this.ulScreen, 'u_tex');
    gl.uniform1f(this.ulScreen.get('u_opacity'), 1.0);
    this.quad(gl);
    gl.disable(gl.BLEND);

    gl.bindVertexArray(savedVAO);
    this.map.triggerRepaint();
  }

  private bindTex(unit: number, tex: WebGLTexture, ul: UL, name: string): void {
    const gl = this.gl;
    gl.activeTexture(gl.TEXTURE0 + unit);
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.uniform1i(ul.get(name), unit);
  }

  private quad(gl: WebGL2RenderingContext): void {
    gl.bindVertexArray(this.quadVAO);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
  }

  private randomState(): Uint8Array {
    const d = new Uint8Array(PARTICLE_RES * PARTICLE_RES * 4);
    for (let i = 0; i < PARTICLE_RES * PARTICLE_RES; i++) {
      const x = Math.floor(Math.random() * 65536);
      const y = Math.floor(Math.random() * 65536);
      d[i * 4]     = x >> 8;
      d[i * 4 + 1] = x & 0xFF;
      d[i * 4 + 2] = y >> 8;
      d[i * 4 + 3] = y & 0xFF;
    }
    return d;
  }

  private loadFlowTex(): void {
    // Use fetch + createImageBitmap with premultiplyAlpha:'none' so the browser
    // does NOT multiply the R/G/B direction channels by the A (TWI) channel.
    // HTMLImageElement-based upload silently premultiplies, which corrupts the
    // encoded flow vectors and makes particles appear static.
    fetch(FLOW_URL)
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.blob();
      })
      .then(blob => createImageBitmap(blob, { premultiplyAlpha: 'none', colorSpaceConversion: 'none' }))
      .then(bitmap => {
        if (!this.active) return;
        const gl  = this.gl;
        const tex = gl.createTexture()!;
        gl.bindTexture(gl.TEXTURE_2D, tex);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S,     gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T,     gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
        gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, gl.RGBA, gl.UNSIGNED_BYTE, bitmap);
        gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false); // leave state clean
        gl.bindTexture(gl.TEXTURE_2D, null);
        this.flowTex = tex;
        this.map.triggerRepaint();
        this.onLoaded?.();
      })
      .catch(e => console.error('[FlowParticleLayer] flow texture load failed:', FLOW_URL, e));
  }
}

// ── Panel layer definitions ───────────────────────────────────────────────────

type OverlayId = 'flow-acc' | 'twi';

interface OverlayDef {
  id:      OverlayId;
  srcId:   string;
  lyrId:   string;
  label:   string;
  desc:    string;
  url:     string;
  opacity: number;
  legend:  { color: string; label: string }[];
}

const OVERLAYS: OverlayDef[] = [
  {
    id:      'flow-acc',
    srcId:   'flow-acc-src',
    lyrId:   'flow-acc-layer',
    label:   'Flow Accumulation',
    desc:    'Log₁₀ drainage area (WhiteboxTools D-Inf). Bright channels carry the most ' +
             'accumulated upstream flow — the primary routing network for water across the Sapi landscape.',
    url:     FLOW_ACC_TILES,
    opacity: 0.80,
    legend: [
      { color: '#deebf7', label: 'Low accumulation'     },
      { color: '#6baed6', label: 'Secondary channels'   },
      { color: '#084594', label: 'Main drainage network' },
    ],
  },
  {
    id:      'twi',
    srcId:   'twi-src',
    lyrId:   'twi-layer',
    label:   'Terrain Wetness',
    desc:    'Topographic Wetness Index (TWI). High values identify topographic depressions ' +
             'and valley floors where soil moisture accumulates — prime targets for hydrology interventions.',
    url:     TWI_TILES,
    opacity: 0.80,
    legend: [
      { color: '#ffffd9', label: 'Dry ridges / slopes' },
      { color: '#41b6c4', label: 'Moderate wetness'    },
      { color: '#081d58', label: 'Valley floors / pools' },
    ],
  },
];

// ── CHIRPS rainfall SVG ───────────────────────────────────────────────────────

function buildRainfallSVG(rows: { date: string; mm: number }[]): SVGSVGElement {
  const ACUTE = 28;
  const W = 360, H = 190, PL = 38, PR = 16, PT = 12, PB = 42;
  const IW = W - PL - PR, IH = H - PT - PB;

  const mmMax = Math.ceil(Math.max(...rows.map(r => r.mm)) / 5) * 5;
  const dateFirst = new Date(rows[0].date).getFullYear();
  const dateLast  = new Date(rows[rows.length - 1].date).getFullYear();
  const totalDays = rows.length;

  const NS = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(NS, 'svg') as SVGSVGElement;
  svg.setAttribute('viewBox', `0 0 ${W} ${H}`);
  svg.setAttribute('width',  String(W));
  svg.setAttribute('height', String(H));
  (svg as unknown as { style: CSSStyleDeclaration }).style.overflow = 'visible';

  const el = <K extends keyof SVGElementTagNameMap>(tag: K, attrs: Record<string, string>) => {
    const e = document.createElementNS(NS, tag);
    Object.entries(attrs).forEach(([k, v]) => e.setAttribute(k, v));
    return e;
  };

  const xOf = (i: number) => PL + (i / (totalDays - 1)) * IW;
  const yOf = (mm: number) => PT + IH * (1 - Math.min(mm, mmMax) / mmMax);

  // Y grid lines at 0, 10, 20, 30 mm (and acute threshold)
  [0, 10, 20, 30].forEach(tick => {
    if (tick > mmMax) return;
    const y = yOf(tick);
    svg.appendChild(el('line', { x1: String(PL), x2: String(W - PR), y1: String(y), y2: String(y), stroke: 'rgba(255,255,255,0.06)', 'stroke-width': '0.5' }));
    const t = el('text', { x: String(PL - 3), y: String(y), 'text-anchor': 'end', 'dominant-baseline': 'middle', 'font-size': '7', fill: '#6b8091' });
    t.textContent = String(tick);
    svg.appendChild(t);
  });

  // Acute threshold dashed line
  const yAcute = yOf(ACUTE);
  svg.appendChild(el('line', { x1: String(PL), x2: String(W - PR), y1: String(yAcute), y2: String(yAcute), stroke: 'rgba(224,123,0,0.55)', 'stroke-width': '1', 'stroke-dasharray': '4 3' }));
  const acuteLbl = el('text', { x: String(W - PR + 2), y: String(yAcute), 'dominant-baseline': 'middle', 'font-size': '6.5', fill: '#e07b00' });
  acuteLbl.textContent = '28 mm';
  svg.appendChild(acuteLbl);

  // Draw polyline segments split by colour
  type Seg = { pts: string[]; color: string };
  const segments: Seg[] = [];
  let cur: Seg | null = null;

  rows.forEach((r, i) => {
    const x = xOf(i), y = yOf(r.mm);
    const frac = Math.min(r.mm / ACUTE, 1);
    // Interpolate #deebf7 → #084594 for sub-acute; orange for acute
    let color: string;
    if (r.mm >= ACUTE) {
      color = '#e07b00';
    } else {
      const rv = Math.round(222 - (222 - 8)   * frac);
      const gv = Math.round(235 - (235 - 69)  * frac);
      const bv = Math.round(247 - (247 - 148) * frac);
      color = `rgb(${rv},${gv},${bv})`;
    }
    if (!cur || cur.color !== color) {
      if (cur && cur.pts.length > 1) segments.push(cur);
      // start new segment, carry last point for continuity
      const prev = cur?.pts[cur.pts.length - 1];
      cur = { pts: prev ? [prev] : [], color };
    }
    cur!.pts.push(`${x.toFixed(1)},${y.toFixed(1)}`);
  });
  if (cur && cur.pts.length > 1) segments.push(cur);

  segments.forEach(seg => {
    svg.appendChild(el('polyline', {
      points: seg.pts.join(' '),
      fill: 'none',
      stroke: seg.color,
      'stroke-width': '0.9',
      'stroke-linejoin': 'round',
    }));
  });

  // X axis year ticks every 5 years
  for (let yr = Math.ceil(dateFirst / 5) * 5; yr <= dateLast; yr += 5) {
    const dayOfYear = Math.floor((yr - dateFirst) / (dateLast - dateFirst + 1) * totalDays);
    const x = xOf(Math.min(dayOfYear, totalDays - 1));
    svg.appendChild(el('line', { x1: String(x), x2: String(x), y1: String(PT + IH), y2: String(PT + IH + 3), stroke: '#6b8091', 'stroke-width': '0.5' }));
    const t = el('text', { x: String(x), y: String(PT + IH + 10), 'text-anchor': 'middle', 'font-size': '7', fill: '#6b8091' });
    t.textContent = String(yr);
    svg.appendChild(t);
  }

  // Axis labels
  const yLbl = el('text', { x: '8', y: String(PT + IH / 2), 'text-anchor': 'middle', 'font-size': '7', fill: '#6b8091', transform: `rotate(-90, 8, ${PT + IH / 2})` });
  yLbl.textContent = 'Rainfall (mm)';
  svg.appendChild(yLbl);

  return svg;
}

// Builds a bar chart of acute rainfall days per year (precip_mm ≥ 28)
function buildAcuteBarSVG(rows: { date: string; mm: number }[]): SVGSVGElement {
  const ACUTE = 28;
  const W = 360, H = 150, PL = 38, PR = 16, PT = 12, PB = 36;
  const IW = W - PL - PR, IH = H - PT - PB;

  // Aggregate by year
  const yearMap = new Map<number, number>();
  rows.forEach(r => {
    if (r.mm < ACUTE) return;
    const yr = new Date(r.date).getFullYear();
    yearMap.set(yr, (yearMap.get(yr) ?? 0) + 1);
  });

  const years = Array.from(yearMap.keys()).sort((a, b) => a - b);
  if (years.length === 0) {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg') as SVGSVGElement;
    svg.setAttribute('viewBox', `0 0 ${W} ${H}`);
    return svg;
  }

  const counts  = years.map(y => yearMap.get(y)!);
  const maxCount = Math.max(...counts);

  const NS = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(NS, 'svg') as SVGSVGElement;
  svg.setAttribute('viewBox', `0 0 ${W} ${H}`);
  svg.setAttribute('width',  String(W));
  svg.setAttribute('height', String(H));
  (svg as unknown as { style: CSSStyleDeclaration }).style.overflow = 'visible';

  const el = <K extends keyof SVGElementTagNameMap>(tag: K, attrs: Record<string, string>) => {
    const e = document.createElementNS(NS, tag);
    Object.entries(attrs).forEach(([k, v]) => e.setAttribute(k, v));
    return e;
  };

  const barW = IW / years.length;

  // Y grid lines
  [0, 0.25, 0.5, 0.75, 1].forEach(f => {
    const y = PT + IH * (1 - f);
    svg.appendChild(el('line', { x1: String(PL), x2: String(W - PR), y1: String(y), y2: String(y), stroke: 'rgba(255,255,255,0.06)', 'stroke-width': '0.5' }));
    if (f > 0) {
      const t = el('text', { x: String(PL - 3), y: String(y), 'text-anchor': 'end', 'dominant-baseline': 'middle', 'font-size': '7', fill: '#6b8091' });
      t.textContent = String(Math.round(maxCount * f));
      svg.appendChild(t);
    }
  });

  // Bars — colour interpolates orange (low) → deep red (high)
  counts.forEach((count, i) => {
    const x    = PL + i * barW;
    const barH = (count / maxCount) * IH;
    const y    = PT + IH - barH;
    const t    = count / maxCount;
    const r    = Math.round(224 + (180 - 224) * t);
    const g    = Math.round(123 + (20  - 123) * t);
    const bv   = 0;
    svg.appendChild(el('rect', {
      x: String(x + 1), y: String(y),
      width:  String(barW - 2),
      height: String(barH),
      fill: `rgb(${r},${g},${bv})`,
      opacity: '0.85',
    }));

    // Year label every 5 years or first/last
    const yr = years[i];
    if (yr % 5 === 0 || i === 0 || i === years.length - 1) {
      const t2 = el('text', { x: String(x + barW / 2), y: String(PT + IH + 10), 'text-anchor': 'middle', 'font-size': '7', fill: '#6b8091' });
      t2.textContent = String(yr);
      svg.appendChild(t2);
    }
  });

  // Y axis label
  const yLbl = el('text', { x: '8', y: String(PT + IH / 2), 'text-anchor': 'middle', 'font-size': '7', fill: '#6b8091', transform: `rotate(-90, 8, ${PT + IH / 2})` });
  yLbl.textContent = 'Acute days / yr';
  svg.appendChild(yLbl);

  // Section label
  const title = el('text', { x: String(PL + IW / 2), y: String(PT - 2), 'text-anchor': 'middle', 'font-size': '7.5', fill: '#a0b8c8' });
  title.textContent = 'Annual acute rainfall events (≥ 28 mm)';
  svg.appendChild(title);

  return svg;
}

// ── SceneFlowDams ─────────────────────────────────────────────────────────────

export class SceneFlowDams implements Scene {
  readonly panel: HTMLElement;
  private mapLegend: HTMLElement | null = null;
  private layer: FlowParticleLayer | null = null;
  private activeOverlay: OverlayId = 'flow-acc';
  private built = false;
  private chirpsBuilt = false;

  constructor(
    private readonly root: HTMLElement,
    private readonly mapAdapter: MapAdapter,
  ) {
    this.panel = document.createElement('div');
    this.panel.className = 'sm-panel';
    this.panel.dataset['scene'] = 'hydrology';
    this.panel.innerHTML = `
      <div class="sm-panel-header">
        <div class="sm-panel-title">Flow, Erosion, and River Habitats</div>
        <div class="sm-panel-sub">D-Infinity · WhiteboxTools · GPU particle system</div>
      </div>

      <div class="sm-flowdams-loading" id="sm-flowdams-loading">
        <div class="sm-flowdams-spinner"></div>
        <span class="sm-flowdams-loading-text">Loading flow field…</span>
      </div>

      <div class="sm-headline">
        Climate change is increasing the frequency and severity of acute
        rainfall events. Active management of erosion and water flows is
        essential to protect river habitats, prevent channel incision, and
        maintain the hydrological conditions that support woody biomass.
      </div>

      <div class="sm-panel-narrative">
        Flow direction is derived from the 5 m COP-DEM using WhiteboxTools
        D-Infinity routing, which distributes flow fractionally across two
        neighbouring cells. Each particle's speed is weighted by local
        flow accumulation — main channels run fast, ridge-tops stall.
        Particles that leave the domain or stall on ridges are
        redistributed, biased toward high-TWI (wet) terrain.
      </div>

      <div class="sm-hydro-layers" style="padding:0 22px;">
        <div class="sm-hydro-layers-label">Map Overlay</div>
        <div class="sm-hydro-layer-btns" id="sm-flowdams-overlay-btns">
          ${OVERLAYS.map(o => `
            <button class="sm-hydro-layer-btn${o.id === 'flow-acc' ? ' active' : ''}"
                    data-overlay="${o.id}">${o.label}</button>`).join('')}
        </div>
        <div class="sm-hydro-layer-desc" id="sm-flowdams-overlay-desc"></div>
      </div>

      <div class="sm-flowdams-stats">
        <div class="sm-flowdams-stat">
          <span>Particles</span><strong>2 000</strong>
        </div>
        <div class="sm-flowdams-stat">
          <span>Flow field</span><strong>512 × 512</strong>
        </div>
        <div class="sm-flowdams-stat">
          <span>Trail fade</span><strong>91%</strong>
        </div>
      </div>

      <div class="sm-hydro-mgmt-divider"></div>
      <div style="padding:0 22px 8px;">
        <div class="sm-hydro-mgmt-insight-title" style="margin-bottom:6px;">
          Daily Rainfall &middot; CHIRPS 1981&ndash;present
        </div>
        <div class="sm-hydro-mgmt-insight-body" style="margin-bottom:10px;">
          CHIRPS satellite rainfall for the Sapi catchment since 1981.
          Orange spikes mark days exceeding the 28&nbsp;mm acute-event threshold &mdash;
          the level at which overland flow and erosion risk increase sharply.
        </div>
        <div id="sm-flow-chirps-wrap">
          <div class="sm-hydro-loading" id="sm-flow-chirps-loading">
            <div class="sm-hydro-spinner"></div>
            <span class="sm-hydro-loading-text">Loading rainfall data&hellip;</span>
          </div>
        </div>
      </div>`;

    root.appendChild(this.panel);

    this.mapLegend = document.createElement('div');
    this.mapLegend.className = 'sm-map-layer-legend';
    this.mapLegend.style.display = 'none';
    document.getElementById('sm-map')?.appendChild(this.mapLegend);
  }

  onEnter(): void {
    if (!this.built) {
      const btns = this.panel.querySelector<HTMLElement>('#sm-flowdams-overlay-btns')!;
      btns.addEventListener('click', (e) => {
        const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('.sm-hydro-layer-btn');
        if (!btn) return;
        btns.querySelectorAll('.sm-hydro-layer-btn').forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        this.showOverlay(btn.dataset['overlay'] as OverlayId);
      });
      this.built = true;
    }

    const loadingEl = this.panel.querySelector<HTMLElement>('#sm-flowdams-loading')!;
    loadingEl.style.display = 'flex';

    const rawMap = this.rawMap();
    this.layer = new FlowParticleLayer(() => {
      loadingEl.style.display = 'none';
    });

    const firstSymbol = rawMap.getStyle().layers?.find(l => l.type === 'symbol')?.id;
    rawMap.addLayer(this.layer as unknown as maplibregl.LayerSpecification, firstSymbol);

    this.showOverlay(this.activeOverlay);

    if (!this.chirpsBuilt) {
      this.chirpsBuilt = true;
      this.buildChirpsChart();
    }
  }

  onLeave(): void {
    const rawMap = this.rawMap();
    try {
      if (rawMap.getLayer('flow-particles')) rawMap.removeLayer('flow-particles');
    } catch { /* already removed */ }
    OVERLAYS.forEach(o => {
      if (rawMap.getLayer(o.lyrId)) rawMap.removeLayer(o.lyrId);
      if (rawMap.getSource(o.srcId)) rawMap.removeSource(o.srcId);
    });
    this.layer = null;
    if (this.mapLegend) this.mapLegend.style.display = 'none';
  }

  private showOverlay(id: OverlayId): void {
    this.activeOverlay = id;
    const rawMap = this.rawMap();

    OVERLAYS.forEach(o => {
      if (rawMap.getLayer(o.lyrId)) rawMap.removeLayer(o.lyrId);
      if (rawMap.getSource(o.srcId)) rawMap.removeSource(o.srcId);
    });

    const def = OVERLAYS.find(o => o.id === id)!;
    rawMap.addSource(def.srcId, { type: 'raster', tiles: [def.url], tileSize: 256 });
    // Insert before particles so particles render on top
    rawMap.addLayer(
      { id: def.lyrId, type: 'raster', source: def.srcId, paint: { 'raster-opacity': def.opacity } },
      'flow-particles',
    );

    const descEl = this.panel.querySelector<HTMLElement>('#sm-flowdams-overlay-desc')!;
    descEl.textContent = def.desc;

    if (this.mapLegend) {
      this.mapLegend.innerHTML =
        `<div class="sm-map-legend-title">${def.label}</div>` +
        def.legend.map(s =>
          `<div class="sm-map-legend-row">
            <span class="sm-map-legend-swatch" style="background:${s.color};border:1px solid rgba(255,255,255,0.12)"></span>
            <span class="sm-map-legend-label">${s.label}</span>
          </div>`
        ).join('');
      this.mapLegend.style.display = 'block';
    }
  }

  private async buildChirpsChart(): Promise<void> {
    const wrap      = this.panel.querySelector<HTMLElement>('#sm-flow-chirps-wrap')!;
    const loadingEl = this.panel.querySelector<HTMLElement>('#sm-flow-chirps-loading')!;
    const CHIRPS_URL = 'https://storage.googleapis.com/centinela-test-data/openclaw/climate/sapi_chirps_daily_zscore_wet_baseline.csv';
    try {
      const res  = await fetch(CHIRPS_URL);
      const text = await res.text();
      const lines = text.trim().split('\n');
      const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
      const dateIdx = headers.indexOf('date');
      const mmIdx   = headers.indexOf('precip_mm');
      if (dateIdx < 0 || mmIdx < 0) { loadingEl.style.display = 'none'; return; }

      const rows = lines.slice(1).map(l => {
        const cols = l.split(',');
        return { date: cols[dateIdx]?.trim() ?? '', mm: parseFloat(cols[mmIdx]) };
      }).filter(r => r.date && isFinite(r.mm));

      loadingEl.style.display = 'none';

      // Daily rainfall line chart (first)
      wrap.appendChild(buildRainfallSVG(rows));

      // Annual acute events bar chart (second)
      const acuteLabel = document.createElement('div');
      acuteLabel.className = 'sm-hydro-mgmt-insight-body';
      acuteLabel.style.cssText = 'margin-top:14px;margin-bottom:4px;';
      acuteLabel.textContent = 'Number of acute rainfall days per year (≥ 28 mm) — rising trend indicates increased erosion and flooding risk.';
      wrap.appendChild(acuteLabel);
      wrap.appendChild(buildAcuteBarSVG(rows));
    } catch (e) {
      loadingEl.style.display = 'none';
      console.error('[SceneFlowDams] CHIRPS fetch failed', e);
    }
  }

  private rawMap(): maplibregl.Map {
    return (this.mapAdapter as unknown as { map: maplibregl.Map }).map;
  }
}
